import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { SectionLabel } from '@/components/px/SectionLabel'
import { adminNav } from '@/components/px/nav'
import { InviteForm } from '@/features/users/components/InviteForm'
import { MembersBrowser } from '@/features/users/components/MembersBrowser'
import { PendingInvitesTable } from '@/features/users/components/PendingInvitesTable'
import { fetchClients } from '@/features/clients/clientsSlice'
import { fetchInvites } from '@/features/users/usersSlice'
import { useAppDispatch } from '@/store'

/**
 * People & access (`/admin/people`, PX-W6.2) — the people administration
 * surface: the invite panel (onboarding step 02), the pending-invites table
 * and the project members browser (step-05 ceiling displayed from server
 * data). Route access is role-gated; the DATA gates stay server-side — a
 * non-admin who reaches the page sees the server's 403 rendered by each
 * panel, never a silently empty screen (AC-7).
 */
export default function PeopleAdminPage() {
  const { t } = useTranslation(['peopleAdmin', 'people'])
  const dispatch = useAppDispatch()

  useEffect(() => {
    void dispatch(fetchInvites())
    // The invite form's optional client binding reads the client register.
    void dispatch(fetchClients())
  }, [dispatch])

  return (
    <AppShell
      moduleLabel={t('people:title')}
      breadcrumb={<span>{t('peopleAdmin:title')}</span>}
      contextPill={t('people:chrome.contextPill')}
      nav={adminNav('admin')}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('peopleAdmin:title')}</h1>
        <p className="text-sm text-muted-foreground">{t('peopleAdmin:lede')}</p>
      </header>

      <div className="mt-5 space-y-4">
        <InviteForm />
        <PendingInvitesTable />
        <MembersBrowser />
      </div>

      <SectionLabel className="mt-6">{t('peopleAdmin:title')}</SectionLabel>
    </AppShell>
  )
}
